import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import UpdateViewDataMutation from "@/shared/graphql/mutations/UpdateViewDataMutation.graphql";
import type { UpdateViewDataMutationType } from "@/types/graphql/site.generated";

const HEARTBEAT_MS = 20_000;

function collectIntervals(
  video: HTMLVideoElement,
): { from: number; to: number }[] {
  const intervals: { from: number; to: number }[] = [];
  for (let i = 0; i < video.played.length; i += 1) {
    intervals.push({ from: video.played.start(i), to: video.played.end(i) });
  }
  return intervals;
}

export function useViewTracking(options: {
  viewToken?: string | null;
  contentId: string;
  isAuthorized: boolean;
  /** Сеанс этого браузера закрыт другим устройством (лимит параллельных
   * просмотров платного контента): плеер останавливается. */
  onViewSessionClosed?: () => void;
}) {
  const { viewToken, contentId, isAuthorized, onViewSessionClosed } = options;
  const [updateViewData] = useMutation<UpdateViewDataMutationType>(
    UpdateViewDataMutation,
  );
  // В ref, чтобы новая функция-колбэк не пересоздавала send и не
  // вызывала финальный флаш эффекта ниже на каждом рендере.
  const onViewSessionClosedRef = useRef(onViewSessionClosed);
  useEffect(() => {
    onViewSessionClosedRef.current = onViewSessionClosed;
  }, [onViewSessionClosed]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Starts at 0, so the very first `timeupdate` always exceeds HEARTBEAT_MS
  // and sends immediately as a baseline — subsequent sends are then spaced
  // ~20s apart. This is intentional, not a bug: "every 20s" excludes this
  // initial baseline send.
  const lastSentRef = useRef(0);

  const send = useCallback(() => {
    const video = videoRef.current;
    if (!video || !viewToken) return;
    const intervals = collectIntervals(video);
    if (intervals.length === 0) return;
    const duration = video.duration || 0;
    void updateViewData({
      variables: {
        trackDuration: { token: viewToken, secondsIntervals: intervals },
        videoPosition: isAuthorized
          ? {
              contentId,
              currentTime: video.currentTime,
              percent:
                duration > 0
                  ? Math.min(100, (video.currentTime / duration) * 100)
                  : 0,
            }
          : null,
      },
    })
      .then((result) => {
        if (result.data?.updateViewData.viewSessionClosed) {
          onViewSessionClosedRef.current?.();
        }
        return result;
      })
      .catch(() => {
        // heartbeat must never break playback
      });
  }, [updateViewData, viewToken, contentId, isAuthorized]);

  const onTimeUpdate = useCallback(
    (video: HTMLVideoElement) => {
      videoRef.current = video;
      const now = Date.now();
      if (now - lastSentRef.current >= HEARTBEAT_MS) {
        lastSentRef.current = now;
        send();
      }
    },
    [send],
  );

  const onPauseOrEnded = useCallback(
    (video: HTMLVideoElement) => {
      videoRef.current = video;
      lastSentRef.current = Date.now();
      send();
    },
    [send],
  );

  const setVideoElement = useCallback((video: HTMLVideoElement | null) => {
    if (video) videoRef.current = video;
  }, []);

  // final flush on unmount
  useEffect(() => () => send(), [send]);

  return { onTimeUpdate, onPauseOrEnded, setVideoElement };
}
